import React, { useState } from "react";
import { searchUser } from "../../services/apiService";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { Loader, Loader2 } from "lucide-react";

const SearchUserPage = () => {
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    email: "",
    mobile: "",
    panNumber: "",
    apiId: "",
  });

  const handleChange = (field, value) => {
    setFilters({
      email: "",
      mobile: "",
      panNumber: "",
      apiId: "",
      [field]: value,
    });

    setSearch(value);
  };

  const [responseData, setResponseData] = useState(null);

  //   const handleChange = (field, value) => {
  //     setFilters((prev) => ({
  //       ...prev,
  //       [field]: value,
  //     }));
  //   };

  const handleSearch = async () => {
    console.log("Search Payload:", filters);

    if (!search) return showErrorToast("enter some value");

    setLoading(true);
    try {
      const res = await searchUser({ search: search });
      if (res?.success) {
        console.log(res);
        setResponseData(res);
        showSuccessToast("Data Fetched Succesfully");
      } else {
        showErrorToast("Nothing Found");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-12 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Search User</h1>

          <p className="text-sm text-gray-500 mt-1">
            Search using Email, Mobile, PAN Number or API ID
          </p>
        </div>
        {/* Search Card */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 md:p-6">
          {/* Top Instruction */}
          <div className="flex items-start gap-3 mb-6 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
            <div className="mt-0.5">
              <div className="w-5 h-5 rounded-full border border-gray-400 flex items-center justify-center text-[11px] text-gray-600 font-semibold">
                i
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-800">
                Search using any one field
              </p>

              <p className="text-xs text-gray-500 mt-1">
                Fill only one input field below — Email, Mobile, PAN Number or
                API ID.
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 items-end">
            {/* Email */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>

              <input
                type="email"
                value={filters.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="Enter email"
                className="w-full h-11 rounded-lg border border-gray-300 px-4 text-sm outline-none focus:border-black transition"
              />

              {/* OR Divider */}
            </div>

            {/* Mobile */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile
              </label>

              <input
                type="text"
                maxLength={10}
                value={filters.mobile}
                onChange={(e) => handleChange("mobile", e.target.value)}
                placeholder="Enter mobile"
                className="w-full h-11 rounded-lg border border-gray-300 px-4 text-sm outline-none focus:border-black transition"
              />
            </div>

            {/* PAN */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                PAN Number
              </label>

              <input
                type="text"
                maxLength={10}
                value={filters.panNumber}
                onChange={(e) =>
                  handleChange("panNumber", e.target.value.toUpperCase())
                }
                placeholder="Enter PAN"
                className="w-full h-11 rounded-lg border border-gray-300 px-4 text-sm uppercase outline-none focus:border-black transition"
              />
            </div>

            {/* API ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API ID
              </label>

              <input
                type="text"
                value={filters.apiId}
                onChange={(e) =>
                  handleChange("apiId", e.target.value.toUpperCase())
                }
                placeholder="Enter API ID"
                className="w-full uppercase h-11 rounded-lg border border-gray-300 px-4 text-sm outline-none focus:border-black transition"
              />
            </div>

            {/* Search Button */}
            <div>
              <button
                type="button"
                onClick={handleSearch}
                className="w-full h-11 flex justify-center items-center rounded-lg bg-blue-700 hover:bg-gray-900 text-white text-sm font-medium transition"
              >
                {loading ? <Loader2 className="animate-spin" /> : "Search"}
              </button>
            </div>
          </div>
        </div>

        {responseData && <ResponseSection responseData={responseData} />}
      </div>
    </div>
  );
};

export default SearchUserPage;

function ResponseSection({ responseData }) {
  const info = responseData?.success ? responseData.data : null;

  return (
    <div className="mt-6 mb-32 w-full max-w-3xl mx-auto bg-slate-50 border border-slate-200 rounded-3xl shadow-xl overflow-hidden transition-all duration-300 hover:shadow-2xl">
      {/* Header with Immersive Gradient */}
      <div
        className={`bg-gradient-to-r ${info?.["KYC Status"] === "Approved" ? "from-emerald-500 via-teal-600 to-cyan-600 " : "from-rose-500 via-red-600 to-orange-600"}  px-6 py-8 text-white relative overflow-hidden`}
      >
        <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute -left-10 -bottom-10 w-40 h-40 bg-black/10 rounded-full blur-2xl pointer-events-none" />

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 relative z-10">
          <div>
            <span className="text-xs font-bold tracking-widest uppercase bg-white/20 px-3 py-1 rounded-full text-emerald-50 backdrop-blur-sm">
              Account Security
            </span>
            <h2 className="text-2xl font-extrabold tracking-tight mt-2 flex items-center gap-2">
              {info?.["Company Name"] !== "N/A"
                ? info?.["Company Name"]
                : "Profile Verification"}
            </h2>
            <p className="text-sm text-teal-100 mt-1">
              API ID:{" "}
              <span className="font-mono bg-black/20 px-1.5 py-0.5 rounded">
                {info?.["API ID"] || "N/A"}
              </span>
            </p>
          </div>

          {/* Big "Wow" Status Badge */}
          {info?.["KYC Status"] === "Approved" ? (
            <div className="flex items-center gap-2 bg-white text-emerald-700 font-bold px-5 py-2.5 rounded-2xl shadow-lg self-start sm:self-center">
              <svg
                className="w-5 h-5 animate-pulse text-emerald-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              KYC Approved
            </div>
          ) : (
            <div className="bg-amber-500 text-white font-bold px-4 py-2 rounded-xl shadow">
              Pending Review
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      {info ? (
        <div className="p-6 sm:p-8 space-y-8">
          {/* Grid 1: Core User Details */}
          <div>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">
              Identity Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-400">
                    Authorized Signatory
                  </p>
                  <p className="text-sm font-semibold text-slate-800">
                    {info?.["Authorized Signatory"] || "Not Provided"}
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-purple-50 text-purple-600 rounded-xl">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-400">
                    Email Address
                  </p>
                  <p className="text-sm font-semibold text-slate-800 font-mono">
                    {info?.Email}
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-400">
                    Mobile Number
                  </p>
                  <p className="text-sm font-semibold text-slate-800 font-mono">
                    {info?.Mobile}
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-400">
                    Digital KYC
                  </p>
                  <span className="inline-flex items-center px-2 py-0.5 mt-0.5 rounded-md text-xs font-semibold bg-emerald-100 text-emerald-800">
                    {info?.["Digital KYC Status"]}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Grid 2: Verification Checklist */}
          <div>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">
              Verification Checklist
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Email", status: info?.["Email Status"] },
                { label: "Mobile", status: info?.["Mobile Status"] },
                { label: "PAN Card", status: info?.["PAN Status"] },
                { label: "Aadhaar", status: info?.["Aadhar Status"] },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white p-3.5 rounded-xl border border-slate-100 shadow-sm flex flex-col items-center text-center justify-between"
                >
                  <span className="text-xs font-semibold text-slate-500 mb-2">
                    {item.label}
                  </span>
                  {item.status === "Verified" ? (
                    <span className="w-full flex items-center justify-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 py-1 px-2 rounded-lg">
                      <svg
                        className="w-3.5 h-3.5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Verified
                    </span>
                  ) : (
                    <span className="w-full text-xs font-bold text-slate-400 bg-slate-100 py-1 px-2 rounded-lg">
                      Unverified
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <hr className="border-slate-200" />

          {/* Timeline / Dates Footer */}
          <div className="flex flex-col sm:flex-row justify-between gap-4 text-xs text-slate-400 font-medium">
            <div className="flex items-center gap-1.5">
              <svg
                className="w-4 h-4 text-slate-400"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 7V3m8 3V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>
                Created:{" "}
                <strong className="text-slate-600 font-mono">
                  {info?.["Creation Date"]}
                </strong>
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <svg
                className="w-4 h-4 text-emerald-500"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>
                Approved:{" "}
                <strong className="text-slate-600 font-mono">
                  {info?.["Approval Date"]}
                </strong>
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-52 text-slate-400 bg-white p-5">
          <svg
            className="w-12 h-12 text-slate-300 mb-2 animate-bounce"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m16.5 0a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 7.5m16.5 0V4.5A2.25 2.25 0 0018 2.25H6A2.25 2.25 0 003.75 4.5V7.5m15.75 1.5V5.37"
            />
          </svg>
          <span className="text-sm font-medium">
            Waiting for response data...
          </span>
        </div>
      )}
    </div>
  );
}
